import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { get, set, del } from 'idb-keyval';
import { draftKey, isDraftKey, type DraftKind } from './drafts';
import { idbKv, type Kv } from './browser';

/** The drafts on screen right now, so a write delivered elsewhere can clear its own. */
const onScreen = new Set<{
  key: string;
  text: () => unknown;
  clear: () => void;
  set: (value: unknown) => void;
}>();

/**
 * Draft persistence (PRD §4.4 rung-1): autosave an in-progress form to IndexedDB, restored
 * on reload — "never lose your work" through the slightest network hiccup.
 *
 * Keyed by its WRITER as well as the entity (MAP 225a, R4): a draft shows only to the person
 * who typed it, and waits for them. `userId` is required, so a draft without one cannot be
 * written.
 *
 * The setter takes the updater form too (`setDraft((d) => ({ ...d, title }))`), and a screen
 * whose draft is an object must use it: two changes in one render (two fields, or a field and
 * a minted id) each spread the RENDER's value otherwise, and the second loses the first.
 *
 * A draft back at its initial value is not kept on the disk (an empty one is no draft).
 */
export function useDraft<T>(
  draft: { kind: DraftKind; userId: string; id: string },
  initial: T,
): [T, Dispatch<SetStateAction<T>>, () => void] {
  const key = draftKey(draft.kind, draft.userId, draft.id);
  const [value, setValue] = useState<T>(initial);
  const loaded = useRef(false);
  const empty = useRef(initial);

  useEffect(() => {
    let active = true;
    void get<T>(key).then((saved) => {
      if (active && saved !== undefined) setValue(saved);
      loaded.current = true;
    });
    return () => {
      active = false;
    };
  }, [key]);

  useEffect(() => {
    if (!loaded.current) return;
    if (Object.is(value, empty.current)) void del(key);
    else void set(key, value);
  }, [key, value]);

  const clear = () => {
    setValue(initial);
    void del(key);
  };

  // On screen, for a note the queue delivers after this screen let go of it.
  // Kept in step with every commit (not after paint), so what is on screen is what is read.
  const latest = useRef({ value, clear });
  useLayoutEffect(() => {
    latest.current = { value, clear };
  });
  useEffect(() => {
    const entry = {
      key,
      text: () => latest.current.value,
      clear: () => latest.current.clear(),
      set: (v: unknown) => setValue(v as T),
    };
    onScreen.add(entry);
    return () => void onScreen.delete(entry);
  }, [key]);

  return [value, setValue, clear];
}

/**
 * Put `value` back in a draft, unless a person has started another one there since (`isFree`
 * says whether what is there may be written over): on the screen if it is showing, and on the
 * disk. Answers the draft that is there afterwards, ours or theirs. "Flag a problem" gives a
 * flag that will not be delivered back to its visit's draft (`lib/ticket-photos.ts`). Best
 * effort, like every draft.
 */
export async function restoreDraft<T>(
  key: string,
  value: T,
  isFree: (current: unknown) => boolean,
  kv: Pick<Kv, 'get' | 'set'> = idbKv,
): Promise<unknown> {
  const shown = [...onScreen].find((d) => d.key === key);
  if (shown) {
    const current = shown.text();
    if (!isFree(current)) return current;
    // The screen's own effect writes it to the disk.
    shown.set(value);
    return value;
  }
  try {
    const current = await kv.get(key);
    if (!isFree(current)) return current;
    await kv.set(key, value);
  } catch {
    // The disk would not answer: nothing more to do than a draft ever could.
  }
  return value;
}

/**
 * The writes that carry a draft, each with its draft kind and the one question that says a
 * draft is still that write's: a note's or a message's text, trimmed as the screen sends it;
 * a flag's ticket id, minted into the draft and sent with it (ticket 03).
 */
const LANDED_DRAFT: Readonly<
  Record<
    string,
    {
      kind: DraftKind;
      isTheWrite: (draft: unknown, variables: Record<string, unknown>) => boolean;
    }
  >
> = {
  'note.append': { kind: 'visit-note', isTheWrite: (d, v) => sameText(d, v.body) },
  'ticket.flag': {
    kind: 'ticket-flag',
    isTheWrite: (d, v) =>
      typeof v.ticketId === 'string' &&
      (d as { ticketId?: unknown } | null | undefined)?.ticketId === v.ticketId,
  },
  'ticket.message': { kind: 'ticket-message', isTheWrite: (d, v) => sameText(d, v.body) },
};

const sameText = (draft: unknown, body: unknown) =>
  typeof draft === 'string' && typeof body === 'string' && draft.trim() === body;

/**
 * A write with a draft landed: the draft it was typed in is cleared, on screen and on the disk,
 * so Save is not offered for the same words again. The screen clears its own draft for a write
 * it still holds; this is what clears it for one the queue delivered after the screen let go (a
 * paused copy after a recheck, one restored at the next start), and is harmless after the
 * screen's own.
 *
 * EXACTLY that draft: the one its screen named (`meta.draftKey`, kept on the queued row), of the
 * kind that write's screen keeps (`LANDED_DRAFT`), and only while it is still that write (a
 * note's text, a flag's ticket id). Another stop's draft saying the same thing is not touched,
 * a draft of another kind is not touched, and a write that names no draft (made before 225b, or
 * by a screen without one) clears nothing. Best effort: a draft left behind is still only a
 * draft.
 */
export async function clearLandedDraft(
  path: string,
  variables: unknown,
  meta: unknown,
  kv: Pick<Kv, 'get' | 'del'> = idbKv,
): Promise<void> {
  const key = (meta as { draftKey?: unknown } | undefined)?.draftKey;
  const rule = Object.hasOwn(LANDED_DRAFT, path) ? LANDED_DRAFT[path]! : null;
  if (!rule || typeof variables !== 'object' || variables === null) return;
  // Read back from the device's storage: only ever a draft of the kind this write keeps.
  if (!isDraftKey(rule.kind, key)) return;
  const isTheWrite = (draft: unknown) =>
    rule.isTheWrite(draft, variables as Record<string, unknown>);
  for (const d of [...onScreen]) {
    if (d.key === key && isTheWrite(d.text())) d.clear();
  }
  try {
    if (isTheWrite(await kv.get(key))) await kv.del(key);
  } catch {
    // The disk would not answer: the draft stays until the person clears it.
  }
}
